"use client";

import { useState, useCallback } from "react";
import type { Editor } from "@tiptap/react";
import type { SelectionState } from "../types";

interface UseLinkHandlersProps {
  editor: Editor | null;
  onSelectionStateChange: (updater: (prev: SelectionState) => SelectionState) => void;
}

export const useLinkHandlers = ({ editor, onSelectionStateChange }: UseLinkHandlersProps) => {
  // Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");

  // Image link modal state
  const [showImageLinkModal, setShowImageLinkModal] = useState(false);
  const [imageLinkUrl, setImageLinkUrl] = useState("");
  const [imageLinkTarget, setImageLinkTarget] = useState<"_blank" | "_self">("_blank");

  const setLink = useCallback(() => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);
    const previousUrl = editor.getAttributes("link")?.href || "";
    setLinkText(selectedText || "");
    setLinkUrl(previousUrl);
    setShowLinkModal(true);
  }, [editor]);

  const handleLinkSubmit = useCallback(() => {
    if (!editor) return;
    if (linkUrl === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl, target: "_blank" }).run();
    }
    setShowLinkModal(false);
    setLinkUrl("");
    setLinkText("");
  }, [editor, linkUrl]);

  const handleLinkClose = useCallback(() => {
    setShowLinkModal(false);
    setLinkUrl("");
    setLinkText("");
  }, []);

  const openImageLinkModal = useCallback(() => {
    if (!editor) return;
    const attrs = editor.getAttributes("image");
    setImageLinkUrl(attrs.link || "");
    setImageLinkTarget(attrs.linkTarget || "_blank");
    setShowImageLinkModal(true);
  }, [editor]);

  const handleImageLinkSubmit = useCallback(() => {
    if (!editor) return;
    const newLink = imageLinkUrl || null;
    editor
      .chain()
      .focus()
      .updateAttributes("image", {
        link: newLink,
        linkTarget: imageLinkTarget,
      })
      .run();
    onSelectionStateChange((prev) => ({ ...prev, currentImageLink: newLink }));
    setShowImageLinkModal(false);
    setImageLinkUrl("");
    setImageLinkTarget("_blank");
  }, [editor, imageLinkUrl, imageLinkTarget, onSelectionStateChange]);

  const removeImageLink = useCallback(() => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .updateAttributes("image", {
        link: null,
        linkTarget: null,
      })
      .run();
    onSelectionStateChange((prev) => ({ ...prev, currentImageLink: null }));
    setShowImageLinkModal(false);
    setImageLinkUrl("");
    setImageLinkTarget("_blank");
  }, [editor, onSelectionStateChange]);

  const handleImageLinkClose = useCallback(() => {
    setShowImageLinkModal(false);
    setImageLinkUrl("");
    setImageLinkTarget("_blank");
  }, []);

  return {
    // Link state
    showLinkModal,
    linkUrl,
    linkText,
    setLinkUrl,
    setLinkText,

    // Image link state
    showImageLinkModal,
    imageLinkUrl,
    imageLinkTarget,
    setImageLinkUrl,
    setImageLinkTarget,

    // Handlers
    setLink,
    handleLinkSubmit,
    handleLinkClose,
    openImageLinkModal,
    handleImageLinkSubmit,
    removeImageLink,
    handleImageLinkClose,
  };
};

